import { computed, type Signal, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { type HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import type { Observable } from 'rxjs';
import type { EntityCrudStrategy, EntityItemResource, EntityMutationResource } from '@zag/ui';
import type { WebhookEndpointResponse } from '@zag/notifications/common';
import { WebhookEndpointService } from '../services/webhook-endpoint.service';

/**
 * Converts an Observable into an `EntityMutationResource` with signal-based state tracking.
 * Mirrors the private `createMutationResource` in `base.resource.ts`.
 */
function mutationFromObservable<T>(observable: Observable<T>): EntityMutationResource<T> {
  const _value = signal<T | null>(null);
  const _isLoading = signal(true);
  const _error = signal<Error | undefined>(undefined);
  const _isSuccess = signal(false);

  observable.subscribe({
    next: result => {
      _value.set(result);
      _isLoading.set(false);
      _isSuccess.set(true);
    },
    error: (err: HttpErrorResponse | Error) => {
      _error.set(err instanceof Error ? err : new Error(err.message || 'Request failed'));
      _isLoading.set(false);
      _isSuccess.set(false);
    },
  });

  return {
    value: _value.asReadonly(),
    isLoading: _isLoading.asReadonly(),
    error: _error.asReadonly(),
    isComplete: computed(() => !_isLoading()),
    isSuccess: _isSuccess.asReadonly(),
  };
}

/**
 * CRUD strategy that wraps `WebhookEndpointService` (Observable-based)
 * into the `EntityCrudStrategy` interface required by `EntityEditFacade`.
 */
export class WebhookCrudStrategy implements EntityCrudStrategy<WebhookEndpointResponse, unknown> {
  constructor(private readonly service: WebhookEndpointService) {}

  load(idSignal: Signal<string | null>): EntityItemResource<WebhookEndpointResponse> {
    const resource = rxResource({
      params: () => ({ id: idSignal() }),
      stream: ({ params }) => {
        if (!params.id) return of(undefined);
        return this.service.getWebhookEndpoint(params.id);
      },
    });

    return {
      value: computed(() => resource.value() ?? null),
      isLoading: resource.isLoading,
      error: computed(() => {
        const e = resource.error();
        if (e instanceof Error) return e;
        if (e) return new Error(String(e));
        return undefined;
      }),
      isLoaded: computed(() => resource.status() === 'resolved'),
    };
  }

  create(payload: unknown): EntityMutationResource<WebhookEndpointResponse> {
    return mutationFromObservable(
      this.service.createWebhookEndpoint(payload as Parameters<WebhookEndpointService['createWebhookEndpoint']>[0])
    );
  }

  update(id: string, payload: unknown): EntityMutationResource<WebhookEndpointResponse> {
    return mutationFromObservable(
      this.service.updateWebhookEndpoint(id, payload as Parameters<WebhookEndpointService['updateWebhookEndpoint']>[1])
    );
  }

  delete(id: string): EntityMutationResource<void> {
    return mutationFromObservable(this.service.deleteWebhookEndpoint(id));
  }
}
